import {
	AspectRatioContainer,
	Breakpoints,
	Image,
	Layout,
	Text,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import gsap from 'gsap';
import * as React from 'react';
import { useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

const HeaderSection = styled.div`
	position: relative;
	background-image: url('https://crm.volkswagen.es/email/tmp/masters_assets/header_background.jpg');
	background-size: cover;
	height: 100vh;
	width: 100%;
	background-position: 20% center;
	z-index: 0;
	overflow: hidden;

	::before {
		content: '';
		background: transparent;
		background: linear-gradient(0deg, var(--c-blue600) 0%, transparent 100%);
		width: 100%;
		height: 30%;
		position: absolute;
		bottom: 0;
	}
`;

const HeaderMaster = styled.div`
	position: absolute;
	top: max(150px, 160px);
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 10;
	width: 140px;
	opacity: 0;

	@media screen and (min-width: 660px) {
		width: 160px;
	}

	@media screen and (min-width: 960px) {
		width: 185px;
	}
`;
const HeaderTitle = styled.div`
	position: absolute;
	top: 57%;
	left: 50%;
	transform: translate(-50%, -50%);
	color: #ffffff;
	text-transform: uppercase;
	opacity: 0;
`;

const HeaderLogo = styled.div`
	position: absolute;
	bottom: 5vh;
	left: 50%;
	transform: translateX(-50%);
	color: #ffffff;
	display: flex;
	flex-direction: column;
	align-items: center;
	opacity: 0;
`;

const Lines = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

// Cada línea ocupa todo el ancho de la pantalla para que pueda "aparecer" de los laterales
const Line = styled.div`
	width: 100vw;
	display: flex;
	justify-content: center;
`;

const LineInner = styled.span`
	display: inline-block;
	will-change: transform, filter;
`;

const StyledText = styled(Text)`
	line-height: 100%;
	font-size: 10vw;
	letter-spacing: 1px;
	-webkit-text-stroke: 2px;
	transform: scale(1.12, 1);

	@media screen and (min-width: 960px) {
		letter-spacing: 2px;
		-webkit-text-stroke: 4px;
	}

	@media screen and (min-width: 1200px) {
		font-size: 6rem;
	}
`;

const StyledVW = styled(Text)`
	line-height: 80%;
`;

export const Header = (): React.ReactElement => {
	const headerImageUrl =
		'https://assets.volkswagen.com/is/image/volkswagenag/header_background?Zml0PWNyb3AlMkMxJndpZD0yNTU5JmhlaT0xNDM5JmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjhkYmM=';
	const masterLogoUrl =
		'https://assets.volkswagen.com/is/image/volkswagenag/badge_master_de_la_carretera-v2?Zml0PWNyb3AlMkMxJndpZD01MTgmaGVpPTUyMSZmbXQ9cG5nLWFscGhhJmJmYz1vZmYmYjNkYQ==';

	const sectionRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLDivElement>(null);
	const lineRefs = useRef<Array<HTMLSpanElement | null>>([]);
	const masterRef = useRef<HTMLDivElement>(null);
	const logoRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!sectionRef.current) return;
		let ctx: gsap.Context | undefined;
		let isCancelled = false;

		const loadImage = (src: string) =>
			new Promise<void>((resolve) => {
				const image = new window.Image();
				image.src = src;

				//Asegurar que la imagen esté cargada antes de iniciar la animación
				if (image.complete && image.naturalWidth > 0) {
					void image
						.decode?.()
						.then(() => resolve())
						.catch(() => resolve());
					return;
				}

				image.onload = () => {
					void image
						.decode?.()
						.then(() => resolve())
						.catch(() => resolve());
				};
				image.onerror = () => resolve();
			});

		const initializeAnimation = () => {
			ctx = gsap.context(() => {
				// Movimiento desde fuera de pantalla
				const fromX = (i: number) => (i % 2 === 0 ? '-110vw' : '110vw');

				// Ocultar master y logo inicialmente
				gsap.set([masterRef.current, logoRef.current], {
					opacity: 0,
				});
				gsap.set(titleRef.current, { opacity: 1 });

				gsap.set(lineRefs.current, {
					x: (i) => fromX(i),
					skewX: (i) => (i % 2 === 0 ? 22 : -22),
					opacity: 0,
					filter: 'blur(14px)',
				});

				// Animación del título
				gsap.to(lineRefs.current, {
					x: '0vw',
					skewX: 0,
					opacity: 1,
					filter: 'blur(0px)',
					duration: 1,
					ease: 'power4.out',
					stagger: 0.12,
					delay: 0.3,
				});

				// Fade in de master y logo después del título
				gsap.to([masterRef.current, logoRef.current], {
					opacity: 1,
					duration: 1.5,
					ease: 'power2.out',
					delay: 1.3,
				});
			}, sectionRef);
		};

		void Promise.all([loadImage(headerImageUrl), loadImage(masterLogoUrl)]).then(() => {
			if (isCancelled) return;
			initializeAnimation();
		});

		return () => {
			isCancelled = true;
			ctx?.revert();
		};
	}, [headerImageUrl, masterLogoUrl]);

	return (
		<Layout
			data-testid="general__layout"
			appearance={{
				[Breakpoints.default]: [
					{ name: '.', columns: 0 },
					{ name: 'a', columns: 24 },
					{ name: '.', columns: 0 },
				],
			}}
			allowOverflowingContent
		>
			<HeaderSection data-testId="header-section" ref={sectionRef}>
				<HeaderMaster data-testId="header-master" ref={masterRef}>
					<AspectRatioContainer data-testid="agenda-aspect-ratio-container" ratio="1:1">
						<Image src={masterLogoUrl} alt="Logo Master" />
					</AspectRatioContainer>
				</HeaderMaster>
				<HeaderTitle data-testId="header-title" ref={titleRef}>
					<Lines data-testId="header-lines">
						<Line data-testId="header-line-0">
							<LineInner ref={(el) => (lineRefs.current[0] = el)}>
								<StyledText color={TokenTextColor.inverted} bold>
									Lleva la
								</StyledText>
							</LineInner>
						</Line>

						<Line data-testId="header-line-1">
							<LineInner ref={(el) => (lineRefs.current[1] = el)}>
								<StyledText color={TokenTextColor.inverted} bold>
									adrenalina al
								</StyledText>
							</LineInner>
						</Line>

						<Line data-testId="header-line-2">
							<LineInner ref={(el) => (lineRefs.current[2] = el)}>
								<StyledText color={TokenTextColor.inverted} bold>
									siguiente nivel
								</StyledText>
							</LineInner>
						</Line>
					</Lines>
				</HeaderTitle>
				<HeaderLogo data-testId="header-logo" ref={logoRef}>
					<StyledVW color={TokenTextColor.inverted} appearance={TokenTextAppearance.headline300} bold>
						Club
					</StyledVW>
					<StyledVW color={TokenTextColor.inverted} appearance={TokenTextAppearance.headline300}>
						Volkswagen
					</StyledVW>
				</HeaderLogo>
			</HeaderSection>
		</Layout>
	);
};
